import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../App';
import { booksApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { 
    PenLine, Plus, BookOpen, Settings, LogOut, Sparkles, CreditCard, GraduationCap, 
    ExternalLink, MoreVertical, Trash2, Flame, Megaphone, Moon, Sun, FileText, Rocket, Upload, Archive, Palette
} from 'lucide-react';
import { toast } from 'sonner';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import DocumentUpload from '../components/DocumentUpload';

const Dashboard = ({ onNavigate, onSelectBook }) => {
    const { user, logout } = useAuth();
    const { theme, toggleTheme } = useTheme();
    const [books, setBooks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showNewBook, setShowNewBook] = useState(false);
    const [showImportBook, setShowImportBook] = useState(false);
    const [bookToDelete, setBookToDelete] = useState(null);
    const [newBook, setNewBook] = useState({
        title: '',
        description: '',
        genre: '',
        target_word_count: 80000,
    });

    useEffect(() => {
        fetchBooks();
    }, []);

    const fetchBooks = async () => {
        try {
            const response = await booksApi.list();
            setBooks(response.data);
        } catch (error) {
            toast.error('Failed to load books');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateBook = async (e) => {
        e.preventDefault();
        try {
            const response = await booksApi.create(newBook);
            setBooks([...books, response.data]);
            setShowNewBook(false);
            setNewBook({ title: '', description: '', genre: '', target_word_count: 80000 });
            toast.success('Book created!');
        } catch (error) {
            toast.error('Failed to create book');
        }
    };

    const handleDeleteBook = async () => {
        if (!bookToDelete) return;
        try {
            await booksApi.delete(bookToDelete.id);
            setBooks(books.filter(b => b.id !== bookToDelete.id));
            toast.success('Book deleted');
        } catch (error) {
            toast.error('Failed to delete book');
        } finally {
            setBookToDelete(null);
        }
    };

    const handleLogout = () => {
        logout();
        onNavigate('landing');
    };

    const genres = [
        'Fantasy', 'Science Fiction', 'Romance', 'Mystery', 'Thriller', 
        'Horror', 'Literary Fiction', 'Historical Fiction', 'Young Adult', 'Non-Fiction'
    ];

    return (
        <div className="min-h-screen bg-background flex">
            {/* Sidebar */}
            <aside className="sidebar-wrapper">
                <div className="px-3 py-4 mb-6">
                    <button 
                        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} 
                        className="brand-logo cursor-pointer hover:opacity-80 transition-opacity"
                        data-testid="logo-home-link"
                        title="My Books (Home)"
                    >
                        <span className="e-prefix text-2xl">e</span>
                        <span className="wordmark text-xl">Skrybe</span>
                    </button>
                </div>

                <nav className="flex-1 space-y-1">
                    <button className="sidebar-item active w-full" data-testid="nav-dashboard">
                        <BookOpen className="h-5 w-5" strokeWidth={1.5} />
                        My Books
                    </button>
                    <button className="sidebar-item w-full" onClick={() => onNavigate('streak')} data-testid="nav-streak">
                        <Flame className="h-5 w-5" strokeWidth={1.5} />
                        Writing Streak
                    </button>
                    <button className="sidebar-item w-full" onClick={() => onNavigate('illustrations')} data-testid="nav-illustrations">
                        <Sparkles className="h-5 w-5" strokeWidth={1.5} />
                        Illustrations
                    </button>
                    <button className="sidebar-item w-full" onClick={() => onNavigate('covers')} data-testid="nav-covers">
                        <Palette className="h-5 w-5" strokeWidth={1.5} />
                        Cover Designer
                    </button>
                    <button className="sidebar-item w-full" onClick={() => onNavigate('promotion')} data-testid="nav-promotion">
                        <Megaphone className="h-5 w-5" strokeWidth={1.5} />
                        Book Promotion
                    </button>
                    <button className="sidebar-item w-full" onClick={() => onNavigate('launch')} data-testid="nav-launch">
                        <Rocket className="h-5 w-5" strokeWidth={1.5} />
                        Launch System
                    </button>
                    <button className="sidebar-item w-full" onClick={() => onNavigate('credits')} data-testid="nav-credits">
                        <CreditCard className="h-5 w-5" strokeWidth={1.5} />
                        Credits
                    </button>
                    <button className="sidebar-item w-full" onClick={() => onNavigate('academy')} data-testid="nav-academy">
                        <GraduationCap className="h-5 w-5" strokeWidth={1.5} />
                        Academy
                    </button>
                    <button className="sidebar-item w-full" onClick={() => onNavigate('resources')} data-testid="nav-resources">
                        <ExternalLink className="h-5 w-5" strokeWidth={1.5} />
                        Resources
                    </button>
                    <button className="sidebar-item w-full" onClick={() => onNavigate('blog')} data-testid="nav-blog">
                        <FileText className="h-5 w-5" strokeWidth={1.5} />
                        Blog
                    </button>
                    <button className="sidebar-item w-full" onClick={() => onNavigate('deleted')} data-testid="nav-deleted">
                        <Archive className="h-5 w-5" strokeWidth={1.5} />
                        Deleted Files
                    </button>
                </nav>

                <div className="border-t border-border pt-4 mt-4">
                    <div className="px-3 py-2 mb-2">
                        <div className="text-sm font-medium">{user?.name}</div>
                        <div className="text-xs text-muted-foreground">{user?.subscription_tier?.toUpperCase() || 'FREE'}</div>
                        <div className="flex items-center gap-2 mt-2">
                            <Sparkles className="h-4 w-4 text-accent" />
                            <span className="text-sm">{user?.credits || 0} credits</span>
                        </div>
                    </div>
                    <button className="sidebar-item w-full" onClick={toggleTheme} data-testid="theme-toggle">
                        {theme === 'dark' ? <Sun className="h-5 w-5" strokeWidth={1.5} /> : <Moon className="h-5 w-5" strokeWidth={1.5} />}
                        {theme === 'dark' ? 'Light Mode' : 'Dark Mode'}
                    </button>
                    <button className="sidebar-item w-full" onClick={() => onNavigate('settings')} data-testid="nav-settings">
                        <Settings className="h-5 w-5" strokeWidth={1.5} />
                        Settings
                    </button>
                    <button className="sidebar-item w-full text-destructive" onClick={handleLogout} data-testid="logout-btn">
                        <LogOut className="h-5 w-5" strokeWidth={1.5} />
                        Log out
                    </button>
                </div>
            </aside>

            {/* Main Content */}
            <main className="flex-1 p-8">
                <div className="max-w-6xl mx-auto">
                    {/* Credits Block */}
                    <div className="mb-6 p-4 rounded-lg bg-gradient-to-r from-accent/10 to-accent/5 border border-accent/20 flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-full bg-accent/20 flex items-center justify-center">
                                <Sparkles className="h-6 w-6 text-accent" />
                            </div>
                            <div>
                                <div className="text-sm text-muted-foreground">Available Credits</div>
                                <div className="text-2xl font-bold text-accent">{user?.credits || 0}</div>
                            </div>
                        </div>
                        <Button 
                            onClick={() => onNavigate('credits')} 
                            className="bg-accent hover:bg-accent/90 text-white"
                            data-testid="buy-credits-btn"
                        >
                            <CreditCard className="h-4 w-4 mr-2" />
                            Buy Credits
                        </Button>
                    </div>

                    {/* Header */}
                    <div className="flex items-center justify-between mb-8">
                        <div>
                            <h1 className="font-serif text-3xl font-semibold">My Books</h1>
                            <p className="text-muted-foreground mt-1">
                                {books.length} {books.length === 1 ? 'book' : 'books'} in your library
                                {books.length > 0 && (
                                    <span className="ml-2 text-accent font-medium">
                                        • {books.reduce((sum, b) => sum + (b.current_word_count || 0), 0).toLocaleString()} total words
                                    </span>
                                )}
                            </p>
                        </div>
                        <div className="flex items-center gap-3">
                            {/* Import Book Dialog */}
                            <Dialog open={showImportBook} onOpenChange={setShowImportBook}>
                                <DialogTrigger asChild>
                                    <Button variant="outline" data-testid="import-book-btn">
                                        <Upload className="h-4 w-4 mr-2" />
                                        Import
                                    </Button>
                                </DialogTrigger>
                                <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
                                    <DialogHeader>
                                        <DialogTitle className="font-serif">Import Manuscript</DialogTitle>
                                    </DialogHeader>
                                    <DocumentUpload 
                                        onBookCreated={(book) => {
                                            fetchBooks();
                                            setShowImportBook(false);
                                            if (book?.book_id) {
                                                onSelectBook({ id: book.book_id, title: book.title });
                                            }
                                        }}
                                        onClose={() => setShowImportBook(false)}
                                    />
                                </DialogContent>
                            </Dialog>
                            
                            {/* New Book Dialog */}
                            <Dialog open={showNewBook} onOpenChange={setShowNewBook}>
                                <DialogTrigger asChild>
                                    <Button className="btn-primary" data-testid="new-book-btn">
                                        <Plus className="h-4 w-4 mr-2" />
                                        New Book
                                    </Button>
                                </DialogTrigger>
                            <DialogContent className="sm:max-w-md">
                                <DialogHeader>
                                    <DialogTitle className="font-serif">Create New Book</DialogTitle>
                                </DialogHeader>
                                <form onSubmit={handleCreateBook} className="space-y-4 mt-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="title">Title</Label>
                                        <Input
                                            id="title"
                                            placeholder="My Amazing Novel"
                                            value={newBook.title}
                                            onChange={(e) => setNewBook({ ...newBook, title: e.target.value })}
                                            required
                                            data-testid="new-book-title"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="genre">Genre</Label>
                                        <Select value={newBook.genre} onValueChange={(v) => setNewBook({ ...newBook, genre: v })}>
                                            <SelectTrigger data-testid="new-book-genre">
                                                <SelectValue placeholder="Select genre" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {genres.map((g) => (
                                                    <SelectItem key={g} value={g}>{g}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="description">Description</Label>
                                        <Textarea
                                            id="description"
                                            placeholder="What's your book about?"
                                            value={newBook.description}
                                            onChange={(e) => setNewBook({ ...newBook, description: e.target.value })}
                                            rows={3}
                                            data-testid="new-book-description"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="target">Target Word Count</Label>
                                        <Input
                                            id="target"
                                            type="number"
                                            value={newBook.target_word_count}
                                            onChange={(e) => setNewBook({ ...newBook, target_word_count: parseInt(e.target.value) })}
                                            data-testid="new-book-target"
                                        />
                                    </div>
                                    <div className="flex justify-end gap-3 pt-4">
                                        <Button type="button" variant="outline" onClick={() => setShowNewBook(false)}>
                                            Cancel
                                        </Button>
                                        <Button type="submit" className="btn-primary" data-testid="create-book-submit">
                                            Create Book
                                        </Button>
                                    </div>
                                </form>
                            </DialogContent>
                        </Dialog>
                        </div>
                    </div>

                    {/* Books Grid */}
                    {loading ? (
                        <div className="flex items-center justify-center py-20">
                            <div className="animate-pulse text-muted-foreground">Loading your books...</div>
                        </div>
                    ) : books.length === 0 ? (
                        <div className="text-center py-20">
                            <BookOpen className="h-16 w-16 mx-auto text-muted-foreground/50 mb-4" strokeWidth={1} />
                            <h2 className="font-serif text-xl font-semibold mb-2">No books yet</h2>
                            <p className="text-muted-foreground mb-6">Create your first book or import an existing manuscript</p>
                            <div className="flex items-center justify-center gap-3">
                                <Button variant="outline" onClick={() => setShowImportBook(true)}>
                                    <Upload className="h-4 w-4 mr-2" />
                                    Import Manuscript
                                </Button>
                                <Button className="btn-primary" onClick={() => setShowNewBook(true)}>
                                    <Plus className="h-4 w-4 mr-2" />
                                    Create New Book
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {books.map((book) => (
                                <BookCard 
                                    key={book.id} 
                                    book={book} 
                                    onClick={() => onSelectBook(book)}
                                    onDelete={() => setBookToDelete(book)}
                                />
                            ))}
                        </div>
                    )}

                    {/* Delete Confirmation Dialog */}
                    <AlertDialog open={!!bookToDelete} onOpenChange={(open) => !open && setBookToDelete(null)}>
                        <AlertDialogContent>
                            <AlertDialogHeader>
                                <AlertDialogTitle>Delete Book</AlertDialogTitle>
                                <AlertDialogDescription>
                                    Are you sure you want to delete "{bookToDelete?.title}"? This action cannot be undone and all chapters, scenes, and notes will be permanently removed.
                                </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction 
                                    onClick={handleDeleteBook}
                                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                    data-testid="confirm-delete-btn"
                                >
                                    Delete Book
                                </AlertDialogAction>
                            </AlertDialogFooter>
                        </AlertDialogContent>
                    </AlertDialog>

                    {/* Academy Link */}
                    <div className="mt-12 pt-8 border-t border-border">
                        <div 
                            className="p-6 rounded-xl bg-gradient-to-r from-accent/10 via-accent/5 to-transparent border border-accent/20 cursor-pointer hover:border-accent/40 transition-colors"
                            onClick={() => onNavigate('academy')}
                            data-testid="dashboard-academy-link"
                        >
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 rounded-full bg-accent/20 flex items-center justify-center">
                                        <GraduationCap className="h-6 w-6 text-accent" />
                                    </div>
                                    <div>
                                        <h3 className="font-serif font-semibold">Writing Mastery Academy</h3>
                                        <p className="text-sm text-muted-foreground">Learn expert writing techniques with AI-powered video lessons</p>
                                    </div>
                                </div>
                                <Button variant="outline" size="sm">
                                    Start Learning
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
};

const BookCard = ({ book, onClick, onDelete }) => {
    const progress = book.target_word_count > 0 
        ? Math.min(100, (book.current_word_count / book.target_word_count) * 100) 
        : 0;

    const coverColors = [
        'bg-gradient-to-br from-red-900 to-red-700',
        'bg-gradient-to-br from-blue-900 to-blue-700',
        'bg-gradient-to-br from-green-900 to-green-700',
        'bg-gradient-to-br from-purple-900 to-purple-700',
        'bg-gradient-to-br from-amber-900 to-amber-700',
    ];
    const colorIndex = book.title.length % coverColors.length;

    return (
        <div 
            className="card-default overflow-hidden hover-lift cursor-pointer group" 
            data-testid={`book-card-${book.id}`}
            onClick={onClick}
        >
            <div className="flex gap-4 p-4">
                {/* Book Cover */}
                <div className={`book-cover w-20 h-28 flex-shrink-0 ${coverColors[colorIndex]} flex items-center justify-center`}>
                    <span className="text-white/80 text-xs font-medium text-center px-2 line-clamp-3">
                        {book.title}
                    </span>
                </div>

                {/* Book Info */}
                <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between">
                        <div>
                            <h3 className="font-serif font-semibold line-clamp-1">{book.title}</h3>
                            {book.genre && (
                                <span className="text-xs text-muted-foreground">{book.genre}</span>
                            )}
                        </div>
                        <DropdownMenu modal={false}>
                            <DropdownMenuTrigger asChild>
                                <Button 
                                    variant="ghost" 
                                    size="icon" 
                                    className="h-8 w-8 opacity-0 group-hover:opacity-100"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                    }}
                                    onPointerDown={(e) => e.stopPropagation()}
                                    data-testid={`book-menu-${book.id}`}
                                >
                                    <MoreVertical className="h-4 w-4" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent 
                                align="end" 
                                onClick={(e) => e.stopPropagation()}
                                onPointerDown={(e) => e.stopPropagation()}
                            >
                                <DropdownMenuItem 
                                    onSelect={(e) => {
                                        e.preventDefault();
                                        onDelete();
                                    }}
                                    className="text-destructive cursor-pointer"
                                    data-testid={`delete-book-${book.id}`}
                                >
                                    <Trash2 className="h-4 w-4 mr-2" />
                                    Delete
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>

                    {book.description && (
                        <p className="text-xs text-muted-foreground line-clamp-2 mt-2">
                            {book.description}
                        </p>
                    )}

                    <div className="mt-3">
                        <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                            <span>{book.current_word_count?.toLocaleString() || 0} words</span>
                            <span>{Math.round(progress)}%</span>
                        </div>
                        <Progress value={progress} className="h-1" />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Dashboard;
